import HomeIcon from '@/assets/icons/sidebar/home.svg?react';
import PersonIcon from '@/assets/icons/sidebar/users.svg?react';
import BrandProtectionIcon from '@/assets/icons/sidebar/brand-protection.svg?react';
import FileIcon from '@/assets/icons/sidebar/file.svg?react';
import SettingsIcon from '@/assets/icons/settings_02.svg?react';
import NotificationIcon from '@/assets/icons/bell.svg?react';
import type { NavItem } from '@/shared/config/navigation';
import { ROUTES } from '@/shared/config/routes';

export type SentraProduct = 'ctem' | 'drp';

const normalizeProduct = (value?: string): SentraProduct => {
  return value?.toLowerCase() === 'drp' ? 'drp' : 'ctem';
};

export const SENTRA_PRODUCT = normalizeProduct(import.meta.env.VITE_PRODUCT);
export const isDrpProduct = SENTRA_PRODUCT === 'drp';

export const productName = isDrpProduct ? 'Sentra DRP' : 'Sentra CTEM';

const drpThreatTypePath = (type: string) => `${ROUTES.BRAND_PROTECTION.VULNS}?threat_type=${type}`;

const drpNavItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'sidebar.items.dashboard',
    path: ROUTES.HOME,
    icon: HomeIcon,
    category: 'menu',
    requiredPermission: 'dashboards:read',
  },
  {
    id: 'drp-brand-threats',
    label: 'sidebar.items.drpBrandThreats',
    icon: BrandProtectionIcon,
    category: 'menu',
    submenus: [
      {
        id: 'drp-threats-all',
        label: 'sidebar.items.allBrandThreats',
        path: ROUTES.BRAND_PROTECTION.VULNS,
        requiredPermission: 'brand:read',
      },
      {
        id: 'drp-threats-scam',
        label: 'sidebar.items.scamThreats',
        path: drpThreatTypePath('scam'),
        requiredPermission: 'brand:read',
      },
      {
        id: 'drp-threats-phishing',
        label: 'sidebar.items.phishingThreats',
        path: drpThreatTypePath('phishing'),
        requiredPermission: 'brand:read',
      },
      {
        id: 'drp-threats-cybersquatting',
        label: 'sidebar.items.cybersquattingThreats',
        path: ROUTES.BRAND_PROTECTION.DOMAINS,
        requiredPermission: 'brand:read',
      },
      {
        id: 'drp-threats-leaks',
        label: 'sidebar.items.leakThreats',
        path: drpThreatTypePath('leaks'),
        requiredPermission: 'brand:read',
      },
      {
        id: 'drp-threats-copyright',
        label: 'sidebar.items.copyrightThreats',
        path: drpThreatTypePath('copyright'),
        requiredPermission: 'brand:read',
      },
      {
        id: 'drp-threats-information',
        label: 'sidebar.items.informationThreats',
        path: drpThreatTypePath('information'),
        requiredPermission: 'brand:read',
      },
    ],
  },
  {
    id: 'drp-persons',
    label: 'sidebar.items.persons',
    path: ROUTES.BRAND_PROTECTION.TOP_MANAGEMENT,
    icon: PersonIcon,
    category: 'menu',
    requiredPermission: 'brand:persons_read',
  },
  {
    id: 'drp-reports',
    label: 'sidebar.items.reports',
    path: ROUTES.REPORTS,
    icon: FileIcon,
    category: 'menu',
    requiredPermission: 'reports:read',
  },
  {
    id: 'drp-brand-settings',
    label: 'sidebar.items.settings',
    path: ROUTES.BRAND_PROTECTION.SETTINGS,
    icon: SettingsIcon,
    category: 'menu',
    requiredPermission: 'brand:manage',
  },
  {
    id: 'notifications',
    label: 'sidebar.items.notifications',
    path: ROUTES.NOTIFICATIONS,
    icon: NotificationIcon,
    category: 'company',
  },
  {
    id: 'organization',
    label: 'sidebar.items.settingsRoot',
    path: ROUTES.SETTINGS.ROOT,
    icon: SettingsIcon,
    category: 'company',
    submenus: [
      {
        id: 'notifications',
        label: 'settingsNav.items.notifications',
        path: ROUTES.SETTINGS.NOTIFICATIONS,
      },
    ],
  },
];

export const getProductNavItems = (items: NavItem[]): NavItem[] => {
  if (!isDrpProduct) {
    return items;
  }

  return drpNavItems;
};

export const getProductHomeRoute = () => {
  return ROUTES.HOME;
};

const drpAllowedCompanyRoutes = new Set<string>([
  ROUTES.HELP.ROOT,
  ROUTES.HELP.SECTION,
  ROUTES.HOME,
  'assistant',
  'reports',
  'reports/editor/:documentId',
  'brand-protection/threats',
  'brand-protection/threats/:id',
  'brand-protection/persons',
  'brand-protection/settings',
  ROUTES.BRAND_PROTECTION.DOMAINS,
  ROUTES.NOTIFICATIONS,
  ROUTES.NOTIFICATIONS_SETTINGS,
  ROUTES.SETTINGS.ROOT,
  ROUTES.SETTINGS.NOTIFICATIONS,
  ROUTES.SETTINGS.OPEN_PORTS,
]);

export const isCompanyRouteAllowedForProduct = (path: string | undefined) => {
  if (!isDrpProduct || !path) {
    return true;
  }
  return drpAllowedCompanyRoutes.has(path);
};
